// Success response
export interface VerificationSuccessResponse {
  verifiedAt: string
}

// Error response types
export type ErrorReason = 'invalidToken' | 'tokenExpired'

export interface VerificationErrorResponse {
  reason: string | ErrorReason
}

// Custom error class for verification errors
export class VerificationError extends Error {
  public readonly statusCode: number
  public readonly reason?: ErrorReason | string

  constructor(message: string, statusCode: number, reason?: ErrorReason | string) {
    super(message)
    this.name = 'VerificationError'
    this.statusCode = statusCode
    this.reason = reason
  }

  isTokenExpired(): boolean {
    return this.reason === 'tokenExpired'
  }

  isTokenInvalid(): boolean {
    return this.reason === 'invalidToken'
  }

  isTooManyRequests(): boolean {
    return this.statusCode === 429
  }

  isServerError(): boolean {
    return this.statusCode === 500
  }
}

// stg-6 / prd-6 live in eu-west-1, stg-5 / prd-5 in eu-central-1
const STAGING_API_BASE_URL_EU6 = 'https://smartgateway-private.eu-west-1.staging.jet-internal.com/applications/mcap/auth'
const PROD_API_BASE_URL_EU6 = 'https://rest.api.eu-west-1.production.jet-external.com/applications/mcap/auth'
const STAGING_API_BASE_URL_EU5 = 'https://smartgateway-private.eu-central-1.staging.jet-internal.com/applications/mcap/auth'
const PROD_API_BASE_URL_EU5 = 'https://rest.api.eu-central-1.production.jet-external.com/applications/mcap/auth'

// Keys are normalised env values (lowercased, separators removed)
const API_BASE_URL_BY_ENV: Record<string, string> = {
  stg5: STAGING_API_BASE_URL_EU5,
  stg6: STAGING_API_BASE_URL_EU6,
  prd5: PROD_API_BASE_URL_EU5,
  prd6: PROD_API_BASE_URL_EU6,
}

const getApiBaseUrl = (env: string | null): string => {
  if (!env) {
    return PROD_API_BASE_URL_EU6
  }

  // Accept stg-6, stg6, STG_6, ... as the same env
  const normalisedEnv = env.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (API_BASE_URL_BY_ENV[normalisedEnv]) {
    return API_BASE_URL_BY_ENV[normalisedEnv]
  }

  // Fall back to staging if env parameter looks like staging (e.g., stg-1, stage)
  if (normalisedEnv.includes('stg') || normalisedEnv.includes('stage')) {
    return STAGING_API_BASE_URL_EU6
  }
  return PROD_API_BASE_URL_EU6
}

export const verifyEmail = async (token: string, env: string | null = null): Promise<VerificationSuccessResponse> => {
  const API_BASE_URL = getApiBaseUrl(env)
  const response = await fetch(`${API_BASE_URL}/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  })

  if (!response.ok) {
    const errorData: VerificationErrorResponse = await response.json().catch(() => ({}))

    switch (response.status) {
      case 400:
        throw new VerificationError(
          'Invalid token format',
          400,
          errorData.reason
        )
      case 401:
        throw new VerificationError(
          errorData.reason === 'tokenExpired'
            ? 'Token has expired'
            : 'Invalid token',
          401,
          errorData.reason as ErrorReason
        )
      case 429:
        throw new VerificationError(
          'Too many requests. Please try again later.',
          429
        )
      case 500:
      default:
        throw new VerificationError(
          'Something went wrong. Please try again later.',
          response.status
        )
    }
  }

  return response.json()
}

export default {
  verifyEmail,
  VerificationError,
}
